import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { useNotifications } from './notifications'; // Import Notifications

const AccountVerificationDialog = ({ isOpen, onClose, onSave, accountData, isNewAccount }) => {
  const [formData, setFormData] = useState({
    acctNo: ''
  });
  const { showSuccess, showError, NotificationComponent } = useNotifications();
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [verificationError, setVerificationError] = useState('');

  const token = localStorage.getItem('token');

  useEffect(() => {
    if (isOpen) {
      // When opening the dialog, always start with a fresh form state
      // We explicitly use an empty string for account number when creating a new account
      setFormData({
        acctNo: accountData && !accountData.acctNo ? '' : (accountData?.acctNo || '')
      });
      
      // Reset all state variables that might trigger submission
      setErrors({});
      setVerificationError('');
      setIsSaving(false); // Ensure we're not in saving state
      
      // Focus on the account number input field when the dialog opens
      setTimeout(() => {
        const inputElement = document.getElementById('acctNo');
        if (inputElement) {
          inputElement.focus();
          // If there's a pre-filled value, select it for easy replacement
          if (inputElement.value) {
            inputElement.select();
          }
        }
      }, 100);
    }
  }, [isOpen, accountData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear any verification error when typing
    if (verificationError) {
      setVerificationError('');
    }
    
    // Clear error for this field when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Account Number validation
    if (!formData.acctNo || formData.acctNo.trim() === '') {
      const isCreatingAccount = accountData && !accountData.acctNo;
      newErrors.acctNo = isCreatingAccount ? 'New Account Number is required' : 'Account Number is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    // First check if we can even submit - this is our additional safeguard
    if (!canSubmitForm() || !validateForm()) {
      return;
    }
    
    // Clear any previous verification error
    setVerificationError('');
    setIsSaving(true);
    
    // Determine if we're creating a new account or verifying existing
    const isCreatingAccount = accountData && !accountData.acctNo;
    
    try {
      // Call the API to verify/create the account
      const response = await fetch('/api/ui/appointment/gCal/verifyAccount', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          acctNo: formData.acctNo,
         
          userId: localStorage.getItem('userId') || '',
          email: localStorage.getItem('userEmail') || '',
          isNewAccount: isCreatingAccount // Let the backend know this is a new account creation
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        // If API call is successful, update local storage and call onSave
        localStorage.setItem('acctNo', formData.acctNo);
        if (result.account && result.account.acctId) {
          localStorage.setItem('acctId', result.account.acctId);
        }
        onSave({ account: result.account || {} });
        // Update URL to new account and reload the page to refresh dashboard
        setTimeout(() => {
          window.history.replaceState(null, '', `?acc=${formData.acctNo}`);
          window.location.reload();
        }, 100);
        // Customize success message based on whether creating new or verifying
        const isCreatingAccount = accountData && !accountData.acctNo;
        const defaultSuccessMsg = isCreatingAccount 
          ? 'Account created successfully!' 
          : 'Account verified and saved successfully';
        showSuccess(result.message || defaultSuccessMsg);
      } else {
        // If API call fails, set the error with appropriate message
        const isCreatingAccount = accountData && !accountData.acctNo;
        const defaultErrorMsg = isCreatingAccount
          ? 'Failed to create account. Please try a different account number.'
          : 'Failed to verify account. Please check your account number and try again.';
        
        const errorMessage = result.message || defaultErrorMsg;
        setVerificationError(errorMessage);
        showError(errorMessage);
        setIsSaving(false);
      }
    } catch (error) {
      console.error('Error processing account:', error);
      const isCreatingAccount = accountData && !accountData.acctNo;
      const errorMessage = isCreatingAccount
        ? 'Failed to create account. Please try again later.'
        : 'Failed to verify account. Please try again later.';
      setVerificationError(errorMessage);
      showError(errorMessage);
      setIsSaving(false);
    }
  };
  
  const handleClose = () => {
    // Reset form when closed
    setFormData({
      acctNo: ''
    });
    setErrors({});
    setVerificationError('');
    setIsSaving(false);
    onClose();
  };

  // Add this new function to determine if the button should be enabled
  const canSubmitForm = () => {
    const isCreatingAccount = accountData && !accountData.acctNo;
    // For creating accounts, need non-empty input
    if (isCreatingAccount) {
      return formData.acctNo && formData.acctNo.trim() !== '';
    }
    // For verification, we might already have a value
    return true;
  };
  
  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog
        as="div"
        className="relative z-10" 
        onClose={() => {}} // Empty function to prevent auto-closing
        static // Add static prop to prevent closing on outside click
      >
        <NotificationComponent />
        
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-4">
                <div className="relative p-0">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="absolute top-1 right-1 p-1 rounded-full bg-black hover:bg-gray-800 transition-colors duration-200 text-white hover:text-gray-200 z-10"
                    aria-label="Close"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
                
                {/* Header with border-bottom */}
                <div className="border-b border-gray-200 py-4 mb-8">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    {accountData && !accountData.acctNo ? 'Create New Account' : 'Account Verification'}
                  </Dialog.Title>
                  {accountData && !accountData.acctNo && (
                    <p className="mt-2 text-sm text-gray-500">
                      Enter a new account number to create a new account.
                    </p>
                  )}
                </div>

                {/* Form Fields */}
                <div className="mt-8 px-4">
                  {/* Verification Error Message */}
                  {verificationError && (
                    <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200">
                      <p className="text-sm text-red-600 text-center font-medium">{verificationError}</p>
                    </div>
                  )}
                
                  {/* Account Number field */}
                  <div className="mb-4">
                    <div className="flex items-center space-x-4">
                      <label 
                        className="block text-sm font-medium text-gray-900 w-1/3"
                        htmlFor="acctNo"
                      >
                        {accountData && !accountData.acctNo ? "Account Number" : "Account Number"} <span className="text-red-600">*</span>
                      </label>
                      <div className="flex-grow">
                        <input
                          id="acctNo"
                          name="acctNo"
                          type="text"
                          value={formData.acctNo}
                          onChange={handleChange}
                          placeholder={accountData && !accountData.acctNo ? "Enter new account number" : "Enter your account number"}
                          className={`block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ${errors.acctNo ? 'ring-red-500 focus:ring-red-500' : 'ring-gray-300 focus:ring-black'} placeholder:text-gray-400 focus:ring-2 focus:ring-inset text-sm`}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              // Only submit if our validation function returns true
                              if (canSubmitForm()) {
                                handleSubmit();
                              }
                            }
                          }}
                        />
                        {errors.acctNo && (
                          <p className="mt-1 text-sm text-red-600">{errors.acctNo}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Button Section with top border */}
                <div className="border-t border-gray-200 mt-10">
                  <div className="flex justify-between mt-5">
                    <button
                      type="button"
                      onClick={handleClose}
                      disabled={isSaving}
                      className={`rounded-md bg-gray-200 text-gray-700 py-2 px-4 ${isSaving ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-300'} focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2`}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={isSaving || !canSubmitForm()}
                      className={`rounded-md ${
                        isSaving || !canSubmitForm()
                        ? "bg-gray-400 cursor-not-allowed" 
                        : "bg-black hover:bg-gray-800"
                      } text-white py-2 px-4 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 min-w-[100px]`}
                    >
                      {isSaving ? (
                        <div className="flex items-center justify-center">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          {accountData && !accountData.acctNo ? 'Creating...' : 'Verifying...'}
                        </div>
                      )                      : (accountData && !accountData.acctNo ? 'Create Account' : 'Verify Account')}
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default AccountVerificationDialog;
